import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {TranslatePipe} from '@ngx-translate/core';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {InsuranceService} from '../../../../core/service/insurance.service';
import {InsuranceDocument, InsuranceOrderDetails} from '../../../../core/models/insurance-order';
import {ToastrService} from 'ngx-toastr';
import {BrowserDetectionService} from '../../../../core/service/browserdetection.service';
import {Browser} from '@capacitor/browser';
import {isMobile} from "../../../../core/helpers/constants";

@Component({
  selector: 'app-insurance-details',
  templateUrl: './insurance-details.component.html',
  styleUrls: ['./insurance-details.component.scss'],
  providers: [TranslatePipe],
})
export class InsuranceDetailsComponent implements OnInit, OnDestroy {
  @Input() initialDetails: InsuranceOrderDetails | null = null;
  @Input() orderId: number;
  @Input() userId: string;

  public details: InsuranceOrderDetails | null = null;
  public isLoading = false;
  public isSyncing = false;
  public isRedirecting = false;
  public expandedDocIndex: number | null = null;
  private ngUnsubscribe = new Subject<void>();

  constructor(
    private insuranceService: InsuranceService,
    private toastr: ToastrService,
    private translate: TranslatePipe,
    private browserDetectionService: BrowserDetectionService,
  ) {}

  ngOnInit(): void {
    if (this.initialDetails) {
      this.details = this.initialDetails;
    } else {
      this.loadDetails();
    }
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  loadDetails(): void {
    this.isLoading = true;
    this.insuranceService
      .getInsuranceOrderDetails(this.userId, this.orderId)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: (d) => {
          this.isLoading = false;
          this.details = d;
        },
        error: () => {
          this.isLoading = false;
          this.details = null;
        }
      });
  }

  getDocuments(): InsuranceDocument[] {
    return this.details?.insuranceMetadata || [];
  }

  getTotalInstallmentsForDoc(doc: InsuranceDocument): number {
    return (doc.installments || []).reduce((s, i) => s + i.value, 0);
  }

  toggleDoc(index: number): void {
    this.expandedDocIndex = this.expandedDocIndex === index ? null : index;
  }

  syncPolicies(): void {
    if (this.isSyncing) { return; }
    this.isSyncing = true;
    this.insuranceService
      .syncInsuranceOrders(this.userId)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: (result) => {
          this.isSyncing = false;
          // Reload details after sync
          this.loadDetails();
          if (result.hasNewPolicies) {
            this.toastr.success(
              this.translate.transform('INSURANCE.SYNC_SUCCESS_NEW'),
              this.translate.transform('INSURANCE.SYNC_TITLE'),
              { toastClass: 'toast ngx-toastr', closeButton: true, timeOut: 3000 }
            );
          } else {
            this.toastr.warning(
              this.translate.transform('INSURANCE.SYNC_NO_NEW'),
              this.translate.transform('INSURANCE.SYNC_TITLE'),
              { toastClass: 'toast ngx-toastr', closeButton: true, timeOut: 3000 }
            );
          }
        },
        error: () => {
          this.isSyncing = false;
          this.toastr.error(
            this.translate.transform('INSURANCE.SYNC_ERROR'),
            this.translate.transform('ERROR'),
            { toastClass: 'toast ngx-toastr', closeButton: true, timeOut: 3000 }
          );
        }
      });
  }

  goToInsurancePlatform(): void {
    if (this.isRedirecting) { return; }
    this.isRedirecting = true;
    this.insuranceService.getAuthToken()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: ({url}) => {
          this.isRedirecting = false;
          if (this.browserDetectionService.isMobileOs() || isMobile()) {
            Browser.open({ url, presentationStyle: 'popover' });
          } else {
            window.open(url, '_blank');
          }
        },
        error: () => {
          this.isRedirecting = false;
          this.toastr.error(
            this.translate.transform('INSURANCE.REDIRECT_ERROR'),
            this.translate.transform('ERROR'),
            { toastClass: 'toast ngx-toastr', closeButton: true, timeOut: 3000 }
          );
        }
      });
  }
}
